import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Inbox, InboxContent, useCounts } from '@novu/react';
import { Avatar, Badge, Button, List, Popover, Tabs } from 'antd';
import dayjs from 'dayjs';
import { useTranslate } from '@/translations';
import { faBell, Icon, Icons } from '@/ui/components';
import { AppNotificationHost } from '@/modules/notifications/AppNotificationHost';
import { NotificationSettingsPanel } from '@/modules/notifications/NotificationSettingsPanel';
import { createDesktopNotificationChannel } from '@/modules/notifications/desktop';
import {
  loadNotificationSettings,
  saveNotificationSettings,
} from '@/modules/notifications/settings';
import { useComms } from '../context/CommsContext';
import { useConversationDrawer } from '../context/ConversationDrawerContext';
import {
  getCallMessagePresentation,
  type ChatMessage,
} from '../matrix/message';
import './GlobalInbox.scss';

interface GlobalInboxProps {
  applicationIdentifier: string;
  subscriber: string;
  callerSubject: string;
  color: string;
  variant?: 'icon' | 'menu';
  onOpenChange?: (open: boolean) => void;
}

interface GlobalInboxContentProps {
  callerSubject: string;
  color: string;
  variant: 'icon' | 'menu';
  onOpenChange?: (open: boolean) => void;
}

const messagePreview = (
  message: ChatMessage | undefined,
  matrixUserId: string | null,
  t: ReturnType<typeof useTranslate>['t'],
) => {
  if (!message) return t('commsNoMessages');
  if (message.kind === 'image') return t('commsImageMessage');
  if (message.kind === 'voice') return t('commsVoiceMessage');
  if (message.kind === 'call') {
    const presentation = getCallMessagePresentation(message, matrixUserId);
    return presentation
      ? t(presentation.key, presentation.values)
      : t('commsNoMessages');
  }
  return message.body;
};

const GlobalInboxContent = ({
  callerSubject,
  color,
  variant,
  onOpenChange,
}: GlobalInboxContentProps) => {
  const { t } = useTranslate();
  const { status, conversations, matrixUserId } = useComms();
  const { openConversation } = useConversationDrawer();
  const { counts } = useCounts({ filters: [{ read: false }] });
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('notifications');
  const [notificationSettings, setNotificationSettings] = useState(() =>
    loadNotificationSettings(callerSubject),
  );
  const desktopChannel = useMemo(createDesktopNotificationChannel, []);
  useEffect(
    () => setNotificationSettings(loadNotificationSettings(callerSubject)),
    [callerSubject],
  );
  const updateNotificationSettings = useCallback(
    (next: typeof notificationSettings) =>
      setNotificationSettings(saveNotificationSettings(callerSubject, next)),
    [callerSubject],
  );
  const openNotifications = useCallback(() => {
    setActiveTab('notifications');
    setOpen(true);
    onOpenChange?.(true);
  }, [onOpenChange]);
  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );
  const novuUnread = counts?.[0]?.count ?? 0;
  const chatUnread = conversations.reduce(
    (total, conversation) => total + conversation.unreadCount,
    0,
  );
  const combinedUnread = novuUnread + chatUnread;

  const chats = useMemo(() => {
    if (status === 'starting' || status === 'reconnecting')
      return (
        <div className="global-inbox__state" role="status">
          {t('commsConnecting')}
        </div>
      );
    if (status === 'failed')
      return (
        <div className="global-inbox__state" role="alert">
          {t('commsChatsUnavailable')}
        </div>
      );
    if (conversations.length === 0)
      return (
        <div className="global-inbox__state">{t('commsNoConversations')}</div>
      );
    return (
      <List
        className="global-inbox__chat-list"
        dataSource={[...conversations]}
        renderItem={(conversation) => {
          const displayName =
            conversation.displayName ?? `Operator ${conversation.operatorId}`;
          const preview = messagePreview(
            conversation.latestMessage,
            matrixUserId,
            t,
          );
          return (
            <List.Item>
              <button
                type="button"
                className="global-inbox__chat"
                aria-label={`${displayName}: ${preview}`}
                onClick={() => {
                  openConversation({
                    userId: conversation.operatorId,
                    displayName,
                  });
                  setOpen(false);
                }}
              >
                <Avatar>{displayName.slice(0, 1).toUpperCase()}</Avatar>
                <span className="global-inbox__chat-copy">
                  <span className="global-inbox__chat-heading">
                    <strong>{displayName}</strong>
                    {conversation.latestMessage && (
                      <time>
                        {dayjs(conversation.latestMessage.timestamp).format(
                          'DD/MM/YYYY HH:mm',
                        )}
                      </time>
                    )}
                  </span>
                  <span className="global-inbox__chat-preview">{preview}</span>
                </span>
                <Badge
                  count={conversation.unreadCount}
                  overflowCount={99}
                  size="small"
                />
              </button>
            </List.Item>
          );
        }}
      />
    );
  }, [conversations, matrixUserId, openConversation, status, t]);

  const content = (
    <div className="global-inbox">
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        tabBarExtraContent={
          <NotificationSettingsPanel
            settings={notificationSettings}
            desktopChannel={desktopChannel}
            onChange={updateNotificationSettings}
          />
        }
        items={[
          {
            key: 'notifications',
            label: t('commsNotifications'),
            children: <InboxContent />,
          },
          {
            key: 'chats',
            label: (
              <Badge count={chatUnread} overflowCount={99} size="small">
                <span>{t('commsChats')}</span>
              </Badge>
            ),
            children: chats,
          },
        ]}
      />
    </div>
  );

  return (
    <>
      <AppNotificationHost
        callerSubject={callerSubject}
        settings={notificationSettings}
        openNotifications={openNotifications}
      />
      <Popover
        content={content}
        trigger="click"
        placement={variant === 'menu' ? 'topRight' : 'bottomRight'}
        open={open}
        onOpenChange={handleOpenChange}
        destroyOnHidden
        arrow={false}
        zIndex={variant === 'menu' ? 2000 : undefined}
        getPopupContainer={variant === 'menu' ? () => document.body : undefined}
        styles={{ body: { padding: 0 } }}
      >
        {variant === 'menu' ? (
          <Button
            type="text"
            className="global-inbox__trigger global-inbox__trigger--menu"
            aria-label={t('commsGlobalInbox')}
            onMouseDown={(event) => {
              event.stopPropagation();
              onOpenChange?.(true);
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <Badge
              count={combinedUnread}
              overflowCount={99}
              size="small"
              data-testid="global-inbox-badge"
            >
              <Icon icon={faBell} />
            </Badge>
            <span>{t('commsGlobalInbox')}</span>
          </Button>
        ) : (
          <Badge
            count={combinedUnread}
            overflowCount={99}
            size="small"
            data-testid="global-inbox-badge"
          >
            <Button
              type="text"
              className="global-inbox__trigger"
              aria-label={t('commsGlobalInbox')}
              icon={<Icon icon={Icons.bell} />}
              style={{ color }}
            />
          </Badge>
        )}
      </Popover>
    </>
  );
};

export const GlobalInbox = ({
  applicationIdentifier,
  subscriber,
  callerSubject,
  color,
  variant = 'icon',
  onOpenChange,
}: GlobalInboxProps) => (
  <Inbox
    applicationIdentifier={applicationIdentifier}
    subscriber={subscriber}
    appearance={{
      variables: { colorPrimary: color },
    }}
  >
    <GlobalInboxContent
      callerSubject={callerSubject}
      color={color}
      variant={variant}
      onOpenChange={onOpenChange}
    />
  </Inbox>
);
